import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../../cn';
import { SNACKBAR_VARIANTS } from '../../constants';

const VARIANT_CLASSES = {
  success: 'bg-green-600',
  error: 'bg-red-600',
  warning: 'bg-yellow-500 text-gray-900',
  info: 'bg-gray-900 bg-opacity-90'
};

const Snackbar = ({
  message,
  type = 'info',
  onClose,
  actionLabel,
  actionName,
  duration = 3000,
  open = true,
  className = ''
}) => {
  const resolvedActionLabel = actionLabel || actionName;
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    setVisible(open);
  }, [open]);

  useEffect(() => {
    if (!visible || !duration) return undefined;

    const timer = setTimeout(() => {
      setVisible(false);
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [visible, duration, onClose]);

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  if (!visible || !message) return null;

  const normalizedType = SNACKBAR_VARIANTS.includes(type) ? type : 'info';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed bottom-4 left-4 z-50 max-w-md rounded-lg px-4 py-3 text-sm text-white shadow-lg',
        VARIANT_CLASSES[normalizedType],
        className
      )}
    >
      <div className="flex items-center gap-3">
        <span className="flex-1">{message}</span>
        {resolvedActionLabel && (
          <button
            type="button"
            className="font-semibold underline underline-offset-2"
            onClick={handleClose}
          >
            {resolvedActionLabel}
          </button>
        )}
      </div>
    </div>
  );
};

Snackbar.displayName = 'Snackbar';

Snackbar.propTypes = {
  message: PropTypes.node.isRequired,
  type: PropTypes.oneOf(SNACKBAR_VARIANTS),
  onClose: PropTypes.func,
  actionLabel: PropTypes.string,
  actionName: PropTypes.string,
  duration: PropTypes.number,
  open: PropTypes.bool,
  className: PropTypes.string
};

export default Snackbar;
